import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import MapPanel from './MapPanel';

const STATE_COLORS = { calm: '#7ecfc0', watch: '#e8c878', alert: '#e8a598', severe: '#e87878' };

function evHash(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h) + id.charCodeAt(i);
  const a = (Math.abs(h) % 360) * (Math.PI / 180);
  const r = 0.12 + (Math.abs(h >> 8) % 55) / 100;
  return {
    top: `${Math.max(8, Math.min(92, 50 + Math.sin(a) * r * 42))}%`,
    left: `${Math.max(8, Math.min(92, 50 + Math.cos(a) * r * 42))}%`,
  };
}

function RadarView({ events, regions, selectedEventId, selectedRegionId, onSelectEvent, onSelectRegion }) {
  const canvasRef = useRef(null);
  const angleRef = useRef(0);
  const rafRef = useRef(null);

  const REGION_POS = {
    'reg-bahamas': { top: '40%', left: '35%' }, 'reg-miami': { top: '35%', left: '30%' },
    'reg-gulf': { top: '30%', left: '20%' }, 'reg-bermuda': { top: '22%', left: '45%' },
    'reg-san-juan': { top: '50%', left: '42%' }, 'reg-cape-verde': { top: '55%', left: '82%' },
    'reg-london': { top: '15%', left: '62%' }, 'reg-paris': { top: '18%', left: '66%' },
    'reg-dubai': { top: '38%', left: '78%' }, 'reg-mumbai': { top: '45%', left: '82%' },
    'reg-singapore': { top: '58%', left: '88%' }, 'reg-tokyo': { top: '28%', left: '92%' },
    'reg-sydney': { top: '72%', left: '92%' }, 'reg-sao-paulo': { top: '68%', left: '40%' },
    'reg-cape-town': { top: '72%', left: '68%' },
  };

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2; c.height = rect.height * 2;
    const ctx = c.getContext('2d');
    ctx.scale(2, 2);
    const w = rect.width, h = rect.height, cx = w / 2, cy = h / 2, R = Math.min(w, h) * 0.42;
    ctx.clearRect(0, 0, w, h);
    for (let i = 1; i <= 5; i++) { ctx.beginPath(); ctx.arc(cx, cy, (R / 5) * i, 0, Math.PI * 2); ctx.strokeStyle = '#e8a59810'; ctx.lineWidth = 0.5; ctx.stroke(); }
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(angleRef.current);
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, R, -0.04, 0.28); ctx.closePath();
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, R); g.addColorStop(0, 'rgba(232,165,152,0)'); g.addColorStop(1, 'rgba(232,165,152,0.1)'); ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(R, 0); ctx.strokeStyle = '#e8a59830'; ctx.lineWidth = 1; ctx.stroke();
    ctx.restore();
    angleRef.current += 0.004;
    rafRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [draw]);

  const topEv = useMemo(() => (events || []).filter(e => !e.id?.startsWith('om-')).slice(0, 8), [events]);

  return (
    <div className="radar-wrap">
      <canvas ref={canvasRef} className="radar-canvas" />
      {(regions || []).map(r => {
        const pos = REGION_POS[r.id] || { top: '50%', left: '50%' };
        const col = STATE_COLORS[r.state] || '#7ecfc0';
        const isSelected = r.id === selectedRegionId;
        return (
          <div key={r.id} className="region-pin" style={{ top: pos.top, left: pos.left }} onClick={() => onSelectRegion(r.id)} data-testid={`region-pin-${r.id}`} title={r.name}>
            <div className={`rpin-dot${r.state !== 'calm' ? ' pulse' : ''}${isSelected ? ' selected' : ''}`} style={{
              background: col, boxShadow: `0 0 ${isSelected ? 20 : 8}px ${col}`,
              borderColor: `${col}80`,
              width: isSelected ? '16px' : r.type === 'primary' ? '12px' : '8px',
              height: isSelected ? '16px' : r.type === 'primary' ? '12px' : '8px',
            }} />
            <span className="rpin-label" style={{ color: col, opacity: isSelected ? 1 : 0.6, fontWeight: isSelected ? '500' : '300' }}>{r.name.split('/')[0].trim().slice(0, 10)}</span>
          </div>
        );
      })}
      {topEv.map(ev => {
        const pos = evHash(ev.id); const col = ev.display?.color || '#7ecfc0'; const sel = ev.id === selectedEventId;
        return (
          <div key={ev.id} className="event-pin" style={{ top: pos.top, left: pos.left }} onClick={() => onSelectEvent(ev.id)} title={ev.title}>
            <div className={`pin-dot${ev.display?.pulse ? ' pulse' : ''}`} style={{ background: col, boxShadow: `0 0 ${sel ? 12 : 5}px ${col}`, borderColor: `${col}80`, width: sel ? '10px' : '5px', height: sel ? '10px' : '5px' }} />
          </div>
        );
      })}
      <div className="radar-label">Live Sensing Map</div>
      <div className="radar-legend">
        <div className="legend-item"><div className="legend-dot" style={{ background: '#7ecfc0' }} /><span className="legend-label">CALM</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#e8c878' }} /><span className="legend-label">WATCH</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#e8a598' }} /><span className="legend-label">ALERT</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#e87878' }} /><span className="legend-label">SEVERE</span></div>
      </div>
    </div>
  );
}

export default function CenterPanel({ events, regions, selectedEventId, selectedRegionId, onSelectEvent, onSelectRegion }) {
  const [view, setView] = useState('map');
  const strip = useMemo(() => (events || []).slice(0, 8), [events]);

  return (
    <div className="center-panel" data-testid="center-panel">
      <div className="view-toggle">
        <button className={`vt-btn${view === 'map' ? ' active' : ''}`} onClick={() => setView('map')} data-testid="view-map-btn">MAP</button>
        <button className={`vt-btn${view === 'radar' ? ' active' : ''}`} onClick={() => setView('radar')} data-testid="view-radar-btn">RADAR</button>
      </div>
      {view === 'map' ? (
        <MapPanel regions={regions} events={events} selectedEventId={selectedEventId} selectedRegionId={selectedRegionId} onSelectEvent={onSelectEvent} onSelectRegion={onSelectRegion} />
      ) : (
        <RadarView events={events} regions={regions} selectedEventId={selectedEventId} selectedRegionId={selectedRegionId} onSelectEvent={onSelectEvent} onSelectRegion={onSelectRegion} />
      )}
      <div className="event-strip" data-testid="event-strip">
        <span className="strip-label">ACTIVE</span>
        {strip.map(ev => {
          const col = ev.display?.color || '#7ecfc0'; const act = ev.id === selectedEventId;
          return (
            <div key={ev.id} className={`strip-item${act ? ' active' : ''}`} style={act ? { borderColor: `${col}40` } : {}} onClick={() => onSelectEvent(ev.id)} data-testid={`strip-item-${ev.id}`}>
              <div className="strip-dot" style={{ background: col }} /><span className="strip-name">{ev.title?.slice(0, 26)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
